import { memo, useEffect } from 'react';
import styles from './termsPage.module.css';

export default memo(function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h1 className={styles.title}>Terms of Use</h1>
        <p className={styles.metaText}>Effective Date: April 23, 2026</p>
        <p className={styles.metaText}>Last Updated: April 23, 2026</p>

        <div className={styles.body}>
          <p className={`${styles.paragraph} ${styles.intro}`}>
            These Terms of Use govern your access to and use of the
            autietravel.com website, including any related pages, features,
            content, comments, reviews, and outbound links (collectively, the
            &quot;Site&quot;). The Site is operated by Autie Travel, Inc.
            (&quot;Autie Travel,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;).
          </p>
          <p className={styles.paragraph}>
            By accessing or using the Site, you agree to these Terms of Use. If
            you do not agree, do not use the Site.
          </p>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. Eligibility</h2>
            <p className={styles.paragraph}>You may use the Site only if:</p>
            <ul className={styles.list}>
              <li>you are at least 18 years old; and</li>
              <li>you have the legal capacity to enter into a binding agreement.</li>
            </ul>
            <p className={styles.paragraph}>
              If you create an account on the Site, you are also responsible for
              maintaining the confidentiality of your login credentials and for
              all activities that occur under your account. You agree to notify
              us immediately of any unauthorized use of your account. We are not
              liable for any loss or damage arising from your failure to
              maintain the security of your credentials.
            </p>
            <p className={styles.paragraph}>
              By using the Site, you represent and warrant that you meet these
              requirements.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Nature of the Site</h2>
            <p className={styles.paragraph}>
              Autie Travel is a travel discovery, review, and booking referral
              platform. The Site may include hotel and travel-related
              information, editorial content, user comments, user reviews,
              ratings, and links to third-party booking providers, including
              Expedia. Users may create and maintain accounts on the Site using
              an email address and password; passwords are securely stored
              using industry-standard hashing. The Site also integrates
              third-party tools and services, including Google Maps for
              location and mapping features and email delivery infrastructure
              (such as SMTP-based services including Nodemailer) for processing
              contact form submissions. Additional third-party integrations may
              be added in the future.
            </p>
            <p className={styles.paragraph}>
              The Site is provided for informational, discovery, referral, and
              review purposes only. Autie Travel does not provide hotel
              accommodations, transportation, or other travel services through
              the Site, and does not process bookings or payments through the
              Site. Autie Travel does not send marketing or promotional emails
              to users.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3. Informational Purposes Only</h2>
            <p className={styles.paragraph}>
              The Site and all content made available through it are provided
              for general informational purposes only.
            </p>
            <p className={styles.paragraph}>
              Content on the Site may include listings, descriptions,
              recommendations, commentary, ratings, reviews, user comments, and
              third-party information. Hotel descriptions, amenities, pricing,
              availability, location details, safety information, accessibility
              information, policies, and other travel-related content may
              change at any time and may be incomplete, inaccurate, outdated,
              subjective, or misleading.
            </p>
            <p className={styles.paragraph}>
              You are solely responsible for evaluating any hotel, destination,
              or service before making travel decisions. You should
              independently verify all material details directly with the
              applicable hotel, provider, or booking platform.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4. No Booking Services; Third-Party Platforms</h2>
            <p className={styles.paragraph}>
              Autie Travel is not a travel agency, booking agent, payment
              processor, or hotel operator through this Site.
            </p>
            <p className={styles.paragraph}>
              If you click a &ldquo;book&rdquo; link or other outbound link on
              the Site, you may be redirected to a third-party website or
              service, including third-party booking platforms such as Expedia.
              Any booking, reservation, payment, cancellation, refund,
              modification, dispute, or customer-service matter will be handled
              solely by the applicable third party. Autie Travel may receive a
              referral fee or commission in connection with outbound booking
              links. Any such arrangement does not affect the price you pay and
              does not create any agency, partnership, or other relationship
              between Autie Travel and the applicable third-party platform.
            </p>
            <p className={styles.paragraph}>
              Your use of third-party services is governed by those third
              parties&apos; own terms, conditions, and privacy practices. We are
              not responsible for the availability, content, policies, security,
              acts, omissions, products, services, or practices of any third
              party.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Third-Party Platforms and Links</h2>
            <p className={styles.paragraph}>
              The Site may contain links to, or embed, third-party websites,
              services, tools, content, and platforms, including reservation
              services and mapping services. These are provided solely for
              convenience. The Site does not use first-party cookies for login,
              session management, or authentication; instead, authentication
              tokens are stored in browser localStorage. Current integrations
              include Google Maps, which is embedded on the Site; when you
              interact with map features, Google may independently collect
              information or use cookies and similar technologies subject to
              Google&apos;s own Privacy Policy and Terms of Service, over which
              we have no control. Additional integrations may be added over
              time.
            </p>
            <p className={styles.paragraph}>
              We do not control third-party websites or services and are not
              responsible for their availability, accuracy, legality, security,
              content, policies, products, services, acts, or omissions. We do
              not guarantee that any third-party reservation platform will
              function properly or remain available.
            </p>
            <p className={styles.paragraph}>
              The Site uses Google Analytics to collect information about how
              users access and interact with the Site. Google Analytics may use
              cookies and similar technologies to collect data such as pages
              visited, time spent on the Site, and general location information.
              For information about how Google uses data collected through its
              services, please visit{' '}
              <a
                className={styles.link}
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer noopener"
              >
                https://policies.google.com/privacy
              </a>
              . Users may opt out of Google Analytics measurement by using the
              Google Analytics Opt-out Browser Add-on available at{' '}
              <a
                className={styles.link}
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noreferrer noopener"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
              . Most browsers also allow users to manage cookies and browser
              storage through browser settings, though blocking certain
              technologies may affect the functionality of embedded features
              such as Google Maps.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6. User Content</h2>
            <p className={styles.paragraph}>
              The Site may permit users to submit, post, upload, transmit, or
              otherwise make available comments, reviews, ratings, profile
              information (if account or profile functionality is offered), or
              other content (&quot;User Content&quot;).
            </p>
            <p className={styles.paragraph}>
              You are solely responsible for your User Content and for the
              consequences of posting or submitting it. By submitting User
              Content, you grant Autie Travel, Inc. a worldwide, royalty-free,
              non-exclusive, perpetual, irrevocable, sublicensable, and
              transferable license to use, reproduce, distribute, display,
              publish, translate, adapt, and create derivative works from your
              User Content in connection with operating, improving, promoting,
              and marketing the Site and Autie&apos;s products and services. You
              represent and warrant that:
            </p>
            <ul className={styles.list}>
              <li>you own or control all rights necessary to submit it;</li>
              <li>it is accurate to the best of your knowledge;</li>
              <li>it does not violate any law or the rights of any person or entity; and</li>
              <li>
                it does not infringe or misappropriate any intellectual
                property, privacy, publicity, or other rights.
              </li>
            </ul>
            <p className={styles.paragraph}>
              We may, but are not required to, monitor, review, remove, edit,
              restrict, or disable User Content at any time and for any reason.
              If you post comments, reviews, or other public content, that
              content may remain visible to others even after a removal request
              in some circumstances, such as where copies, caches, screenshots,
              reposts, or archival versions exist outside our control.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7. Prohibited Conduct</h2>
            <p className={styles.paragraph}>You agree not to:</p>
            <ul className={styles.list}>
              <li>use the Site for any unlawful, fraudulent, deceptive, harmful, or abusive purpose;</li>
              <li>post false, defamatory, harassing, threatening, obscene, hateful, or otherwise objectionable content;</li>
              <li>impersonate any person or entity or misrepresent your affiliation;</li>
              <li>interfere with the operation, security, or functionality of the Site;</li>
              <li>attempt to gain unauthorized access to the Site, accounts, servers, or systems;</li>
              <li>use automated tools, bots, scrapers, or similar methods to access, monitor, extract, or copy Site content or data;</li>
              <li>collect information about other users without authorization;</li>
              <li>use the Site to advertise, spam, solicit, or promote unrelated commercial activity without our written consent;</li>
              <li>upload malicious code or otherwise damage or disrupt the Site; or</li>
              <li>use the Site in a way that could create liability for Autie Travel or others.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8. Intellectual Property</h2>
            <p className={styles.paragraph}>
              The Site and its design, layout, software, text, graphics, logos,
              trademarks, compilations, and other content, excluding User
              Content, are owned by or licensed to Autie Travel and are
              protected by intellectual property and other applicable laws.
            </p>
            <p className={styles.paragraph}>
              Subject to these Terms, we grant you a limited, revocable,
              non-exclusive, non-transferable license to access and use the Site
              for personal, non-commercial use only.
            </p>
            <p className={styles.paragraph}>
              You may not reproduce, distribute, modify, create derivative works
              from, publicly display, publicly perform, republish, download,
              store, transmit, sell, license, commercially exploit, or otherwise
              use any part of the Site for commercial purposes except as
              expressly permitted by these Terms or by law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9. Feedback</h2>
            <p className={styles.paragraph}>
              If you provide suggestions, ideas, comments, or other feedback
              regarding the Site, you grant us a perpetual, irrevocable,
              worldwide, royalty-free, fully paid, sublicensable right to use,
              reproduce, modify, adapt, publish, translate, distribute, and
              otherwise exploit that feedback for any lawful purpose without
              compensation or attribution to you.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. Privacy</h2>
            <p className={styles.paragraph}>
              Your use of the Site is also subject to the Autie Travel Privacy
              Policy located at{' '}
              <a className={styles.link} href="/privacy-policy">
                /privacy-policy
              </a>
              , which describes how we collect, use, share, disclose, and
              retain information in connection with the Site, including: the
              categories of information we collect automatically or that you
              provide directly (including information provided when creating an
              account); how we use and share that information; our use of
              cookies and similar technologies; California privacy disclosures
              and rights; data security practices; and how to submit
              privacy-related requests. We do not share your information with
              third-party partners for their own marketing purposes. The Site
              does not use first-party cookies for login or session management;
              instead, authentication tokens are stored in browser
              localStorage. The Site uses Google Analytics to measure Site
              traffic and user behavior; Google Analytics may use cookies and
              similar technologies in connection with that measurement. Certain
              embedded third-party services, including Google Maps, may also
              independently place cookies or use similar tracking technologies
              subject to their own policies. We retain information for as long
              as reasonably necessary to operate the Site, fulfill the purposes
              described in our Privacy Policy, and comply with applicable legal
              obligations. We do not operate automated data deletion schedules.
              Account information and associated data are generally retained
              until you request deletion of your account or we otherwise
              determine in our sole discretion that retention is no longer
              necessary. We may, but are not obligated to, delete or anonymize
              certain information at our discretion. Please review our Privacy
              Policy for full details, including how to submit a data deletion
              request.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>11. Disclaimers</h2>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              To the maximum extent permitted by law, the Site and all content,
              features, and functionality are provided &quot;as is&quot; and
              &quot;as available,&quot; without warranties of any kind, whether
              express, implied, or statutory.
            </p>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              Autie Travel disclaims all warranties, including any implied
              warranties of merchantability, fitness for a particular purpose,
              title, non-infringement, accuracy, availability, quiet enjoyment,
              and any warranties arising out of course of dealing, usage, or
              trade.
            </p>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              Without limiting the foregoing, Autie Travel does not warrant
              that:
            </p>
            <ul className={`${styles.list} ${styles.legalEmphasis}`}>
              <li>the Site will be available, uninterrupted, secure, or error-free;</li>
              <li>any content will be accurate, complete, reliable, current, or suitable;</li>
              <li>any hotel, destination, or third-party service will meet your expectations; or</li>
              <li>defects will be corrected.</li>
            </ul>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              Your use of the Site is at your sole risk.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. Limitation of Liability</h2>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              To the fullest extent permitted by law, Autie Travel, Inc. and its
              officers, directors, employees, agents, contractors, affiliates,
              successors, and assigns will not be liable for any indirect,
              incidental, special, consequential, exemplary, or punitive
              damages, or for any loss of profits, revenue, data, goodwill,
              business opportunity, or other intangible losses, arising out of
              or relating to:
            </p>
            <ul className={`${styles.list} ${styles.legalEmphasis}`}>
              <li>your access to or use of, or inability to access or use, the Site;</li>
              <li>your reliance on any content or information on the Site;</li>
              <li>any hotel, destination, provider, or third-party platform;</li>
              <li>any booking, reservation, payment, cancellation, refund, dispute, or transaction with a third party;</li>
              <li>any User Content or conduct of other users or third parties; or</li>
              <li>any unauthorized access to or use of the Site or data.</li>
            </ul>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              To the fullest extent permitted by law, the total liability of
              Autie Travel for any claim arising out of or relating to the Site
              or these Terms will not exceed one hundred U.S. dollars (US $100).
              Notwithstanding the foregoing, this limitation does not apply to
              liability arising from Autie Travel, Inc.&apos;s own fraud, gross
              negligence, or willful misconduct.
            </p>
            <p className={`${styles.paragraph} ${styles.legalEmphasis}`}>
              Some jurisdictions do not allow certain limitations of liability,
              so some of the above limitations may not apply to you.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>13. Indemnification</h2>
            <p className={styles.paragraph}>
              You agree to defend, indemnify, and hold harmless Autie Travel,
              Inc. and its officers, directors, employees, agents, contractors,
              affiliates, successors, and assigns from and against any claims,
              demands, actions, proceedings, damages, judgments, settlements,
              penalties, fines, losses, liabilities, costs, and expenses,
              including reasonable attorneys&apos; fees, arising out of or
              relating to:
            </p>
            <ul className={styles.list}>
              <li>your access to or use of the Site;</li>
              <li>your User Content;</li>
              <li>your violation of these Terms;</li>
              <li>your violation of any law or third-party right;</li>
              <li>your dealings with any hotel, provider, platform, or user; or</li>
              <li>any allegation that your conduct or content caused harm or loss to a third party.</li>
            </ul>
            <p className={styles.paragraph}>
              We reserve the right to assume the exclusive defense and control
              of any matter otherwise subject to indemnification by you, and
              you agree to cooperate with us in that defense.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>14. Release</h2>
            <p className={styles.paragraph}>
              To the fullest extent permitted by law, you release Autie Travel
              and its affiliates, officers, directors, employees, agents, and
              contractors from any and all claims, demands, and damages arising
              out of or relating to disputes between you and:
            </p>
            <ul className={styles.list}>
              <li>any hotel, travel provider, booking provider, or other third party;</li>
              <li>any other user; or</li>
              <li>any service or transaction you access through the Site.</li>
            </ul>
            <p className={styles.paragraph}>
              You expressly waive the protections of California Civil Code
              Section 1542 to the fullest extent permitted by applicable law.
              Section 1542 provides: &quot;A general release does not extend to
              claims that the creditor or releasing party does not know or
              suspect to exist in his or her favor at the time of executing the
              release and that, if known by him or her, would have materially
              affected his or her settlement with the debtor or released
              party.&quot;
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>15. Termination and Suspension</h2>
            <p className={styles.paragraph}>
              We may suspend, restrict, or terminate your access to all or part
              of the Site at any time, with or without notice, for any reason or
              no reason, including if we believe you have violated these Terms
              or your conduct creates legal, reputational, security, or
              operational risk. Provisions of these Terms that by their nature
              should survive termination will survive, including Sections 6
              (User Content), 8 (Intellectual Property), 9 (Feedback), 12
              (Limitation of Liability), 13 (Indemnification), 14 (Release), 17
              (Governing Law and Venue), and 18 (Miscellaneous).
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>16. Changes to the Site or These Terms</h2>
            <p className={styles.paragraph}>
              We may modify, suspend, discontinue, or restrict any part of the
              Site at any time without liability.
            </p>
            <p className={styles.paragraph}>
              We may also update these Terms from time to time to reflect
              changes in the Site, legal requirements, business practices, or
              third-party services. We will notify you of material changes by
              posting a notice on the Site and may, in our discretion, send an
              email notification if you have provided an email address. Updated
              Terms will become effective no sooner than thirty (30) days after
              they are posted on the Site, unless a different effective date is
              stated or the changes are required by law or necessary
              circumstance. Your continued use of the Site after updated Terms
              become effective constitutes your acknowledgment and acceptance
              of the revised Terms, to the extent permitted by law.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>17. Governing Law and Venue</h2>
            <p className={styles.paragraph}>
              These Terms and any dispute arising out of or relating to the Site
              or these Terms are governed by the laws of the State of
              California, without regard to conflict-of-law principles.
            </p>
            <p className={styles.paragraph}>
              To the extent permitted by law, any lawsuit or proceeding arising
              out of or relating to the Site or these Terms must be brought
              exclusively in the state or federal courts located in San
              Francisco, California, and you consent to the personal
              jurisdiction of those courts.
            </p>
            <p className={styles.paragraph}>
              If California law provides any non-waivable consumer protections
              that apply to you, nothing in these Terms is intended to limit
              those protections.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>18. Miscellaneous</h2>
            <p className={styles.paragraph}>
              These Terms, together with the Privacy Policy and any other
              policies or notices expressly incorporated by reference,
              constitute the entire agreement between you and Autie Travel
              regarding the Site.
            </p>
            <p className={styles.paragraph}>
              If any provision of these Terms is found unenforceable, the
              remaining provisions will remain in full force and effect.
            </p>
            <p className={styles.paragraph}>
              Our failure to enforce any provision of these Terms is not a
              waiver of that provision.
            </p>
            <p className={styles.paragraph}>
              You may not assign or transfer your rights or obligations under
              these Terms without our prior written consent. We may assign these
              Terms without your consent in connection with a merger,
              acquisition, restructuring, sale of assets, or transfer to an
              affiliate or subsidiary.
            </p>
            <p className={styles.paragraph}>
              Section headings are for convenience only and do not affect
              interpretation.
            </p>
            <p className={styles.paragraph}>
              We are committed to making the Site accessible to users with
              disabilities consistent with applicable law, including the
              California Unruh Civil Rights Act and the Americans with
              Disabilities Act. If you encounter an accessibility barrier on
              the Site, please contact us at{' '}
              <a className={styles.link} href="mailto:sam@autietravel.com">
                sam@autietravel.com
              </a>{' '}
              so we may address it.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>19. Contact Information</h2>
            <p className={styles.paragraph}>
              If you have questions about these Terms, please contact:
            </p>
            <div className={styles.contactBlock}>
              <p className={styles.paragraph}>
                <strong>Autie Travel, Inc.</strong>
                <br />
                Attn: Privacy Inquiries
                <br />
                31 Corte Morada, Greenbrae, CA 94904
                <br />
                <a className={styles.link} href="mailto:sam@autietravel.com">
                  sam@autietravel.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
});
